import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useRetailerPages } from '../../hooks/useRetailerPages';

/**
 * MenuBlock V4 - Navigation Full-Width avec Container Centré
 * 
 * ✅ REFACTOR STRUCTUREL MAI 2026:
 * - ALIGNÉ sur HeaderBlock: utilise le même container centré
 * - max-w-7xl (1280px) = référence design system
 * - Comportement zoom stable (plus de décalage)
 * 
 * ✅ CONTROL PLANE SYNC:
 * - Utilise useRetailerPages() comme source unique
 * - Affiche HUB pages métier + pages CMS
 * - Respecte visibilité et ordre du Control Plane
 * 
 * ✅ DESKTOP :
 * - Container centré max-w-7xl (identique au Header)
 * - Items en flex avec espacement équilibré
 * - Texte centré avec ellipsis si trop long
 * 
 * ✅ MOBILE :
 * - Dropdown vertical avec scroll
 * - Container centré identique
 * 
 * SAFE MODE: Read-only, aucune modification de routes ou backend.
 */
const MenuBlock = ({ block }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Control Plane: Source unique pour la navigation
  const { hubPages, cmsPages, loading } = useRetailerPages();
  
  // Construire la liste des pages pour le menu
  const pages = React.useMemo(() => {
    const menuItems = [];
    
    // Ajouter les pages HUB métier
    hubPages.forEach(page => {
      menuItems.push({
        id: page.id,
        title: page.title,
        slug: page.route,
        menu_order: page.display_order
      });
    });
    
    // Ajouter les pages CMS
    cmsPages.forEach(page => {
      menuItems.push({
        id: page.id,
        title: page.title,
        slug: page.route,
        menu_order: page.display_order
      });
    });
    
    // Trier par display_order
    return menuItems.sort((a, b) => a.menu_order - b.menu_order);
  }, [hubPages, cmsPages]);

  // Fermer le menu mobile lors du changement de page
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (slug) => {
    return location.pathname === slug || location.pathname.startsWith(`${slug}/`);
  };

  // Chargement
  if (loading) {
    return (
      <nav className="border-b bg-background">
        {/* Container centré identique au Header */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="animate-pulse h-10 bg-gray-200 rounded w-1/3"></div>
        </div>
      </nav>
    );
  }

  // Fallback si aucune page
  if (pages.length === 0) {
    console.warn('⚠️  [MenuBlock] Aucune page disponible pour le menu');
    
    if (process.env.NODE_ENV === 'development') {
      return (
        <nav className="border-b bg-yellow-50">
          <div className="max-w-7xl mx-auto px-4 py-3 text-sm text-yellow-700">
            ⚠️ Menu vide : Aucune page avec "Afficher dans le menu" activé
          </div>
        </nav>
      );
    }
    
    return null;
  }

  return (
    <>
      {/* Desktop Menu - Container Centré (aligné sur Header) */}
      <nav className="hidden md:block w-full border-b bg-background shadow-sm">
        {/* 
          REFACTOR V4: Container centré max-w-7xl
          - Identique au HeaderBlock
          - Comportement zoom stable
          - Design system unifié
        */}
        <div className="max-w-7xl mx-auto px-4">
          <ul className="flex items-stretch justify-center">
            {pages.map((page) => (
              <li key={page.id} className="flex-shrink-0">
                <Link
                  to={page.slug || `page-${page.id}` || '/'}
                  className={`
                    flex items-center justify-center text-center
                    px-4 py-3 h-full
                    text-sm font-medium transition-colors
                    hover:text-primary hover:bg-accent/50
                    whitespace-nowrap
                    ${isActive(page.slug) 
                      ? 'text-primary bg-accent border-b-2 border-primary' 
                      : 'text-foreground border-b-2 border-transparent'
                    }
                  `}
                  title={page.title || 'Page sans titre'}
                  data-testid={`menu-link-${(page.slug || `page-${page.id}` || 'unknown').replace(/\//g, '-')}`}
                >
                  {page.title || 'Page sans titre'}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Mobile Menu Button - Container Centré */}
      <div className="md:hidden w-full border-b bg-background shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full justify-between"
            data-testid="mobile-menu-toggle"
          >
            <span className="flex items-center gap-2">
              <Menu className="h-4 w-4" />
              Menu
            </span>
            {mobileMenuOpen ? (
              <X className="h-4 w-4" />
            ) : (
              <span className="text-xs text-muted-foreground">
                {pages.length} pages
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Menu Dropdown - Container Centré */}
      {mobileMenuOpen && (
        <nav className="md:hidden fixed top-[112px] left-0 right-0 z-30 border-b bg-background shadow-lg max-h-[calc(100vh-112px)] overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 pb-4">
            <ul className="space-y-1">
              {pages.map((page) => (
                <li key={page.id}>
                  <Link
                    to={page.slug || `page-${page.id}` || '/'}
                    className={`
                      block px-4 py-3 rounded-lg text-sm font-medium transition-colors
                      ${isActive(page.slug) 
                        ? 'bg-primary text-primary-foreground' 
                        : 'hover:bg-accent hover:text-accent-foreground'
                      }
                    `}
                    data-testid={`menu-link-mobile-${(page.slug || `page-${page.id}` || 'unknown').replace(/\//g, '-')}`}
                  >
                    {page.title || 'Page sans titre'}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </nav>
      )}
    </>
  );
};

export default MenuBlock;
